import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Clock, Play, Quote, FileText, ExternalLink } from 'lucide-react';

interface TimelineItem {
  timestamp: string;
  timeInSeconds: number;
  title: string;
  summary: string;
  keyQuotes: string[];
  topics: string[];
}

const Index = () => {
  const [activeTimestamp, setActiveTimestamp] = useState<string>('0:00');
  const timelineRef = useRef<HTMLDivElement>(null);

  // Sample timeline data - this would be populated from video analysis
  const timelineData: TimelineItem[] = [
    {
      timestamp: '0:00',
      timeInSeconds: 0,
      title: 'Introduction & Overview',
      summary: 'Opening remarks and introduction to the main topic of discussion.',
      keyQuotes: ['Welcome to today\'s discussion', 'We\'ll be covering several key points'],
      topics: ['Introduction', 'Overview']
    },
    {
      timestamp: '2:30',
      timeInSeconds: 150,
      title: 'First Main Topic',
      summary: 'Deep dive into the first major concept or theme presented in the video.',
      keyQuotes: ['This is a crucial point to understand', 'The implications are significant'],
      topics: ['Main Concept', 'Analysis']
    },
    {
      timestamp: '5:45',
      timeInSeconds: 345,
      title: 'Supporting Evidence',
      summary: 'Presentation of data, examples, or evidence supporting the main arguments.',
      keyQuotes: ['The data clearly shows', 'For example, consider this case'],
      topics: ['Evidence', 'Examples', 'Data']
    },
    {
      timestamp: '8:20',
      timeInSeconds: 500,
      title: 'Counter-Arguments',
      summary: 'Discussion of alternative viewpoints or potential objections to the main thesis.',
      keyQuotes: ['However, some might argue', 'It\'s important to consider the opposing view'],
      topics: ['Counter-arguments', 'Alternative Views']
    },
    {
      timestamp: '11:10',
      timeInSeconds: 670,
      title: 'Practical Applications',
      summary: 'How the discussed concepts can be applied in real-world scenarios.',
      keyQuotes: ['In practice, this means', 'You can apply this by'],
      topics: ['Applications', 'Practical Use']
    },
    {
      timestamp: '14:30',
      timeInSeconds: 870,
      title: 'Conclusion & Key Takeaways',
      summary: 'Summary of main points and final thoughts on the topic.',
      keyQuotes: ['To summarize the key points', 'The most important takeaway is'],
      topics: ['Conclusion', 'Summary', 'Takeaways']
    }
  ];

  const handleTimestampClick = (timestamp: string, timeInSeconds: number) => {
    setActiveTimestamp(timestamp);
    // This would jump to the specific time in the YouTube video
    const youtubeUrl = `https://www.youtube.com/watch?v=2UBeQ0Z-OMI&t=${timeInSeconds}s`;
    window.open(youtubeUrl, '_blank');
  };

  const scrollToTimestamp = (timestamp: string) => {
    const element = document.getElementById(`timestamp-${timestamp.replace(':', '-')}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                Video Analysis Timeline
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                Interactive breakdown of YouTube video content with timestamps
              </p>
            </div>
            <Button 
              onClick={() => window.open('https://www.youtube.com/watch?v=2UBeQ0Z-OMI', '_blank')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Watch Original Video
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* YouTube Player */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Video Player
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/2UBeQ0Z-OMI"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded-lg"
                  ></iframe>
                </div>
                <div className="mt-4 space-y-2">
                  <h3 className="font-semibold text-sm text-slate-700 dark:text-slate-300">
                    Quick Navigation
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {timelineData.map((item) => (
                      <Button
                        key={item.timestamp}
                        variant={activeTimestamp === item.timestamp ? "default" : "outline"}
                        size="sm"
                        onClick={() => scrollToTimestamp(item.timestamp)}
                        className="text-xs"
                      >
                        {item.timestamp}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Timeline */}
          <div className="lg:col-span-2" ref={timelineRef}>
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-5 h-5 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Content Timeline
                </h2>
              </div>

              {timelineData.map((item, index) => (
                <div
                  key={item.timestamp}
                  id={`timestamp-${item.timestamp.replace(':', '-')}`}
                  className="relative"
                >
                  {/* Timeline line */}
                  {index < timelineData.length - 1 && (
                    <div className="absolute left-6 top-16 w-0.5 h-full bg-gradient-to-b from-blue-500 to-blue-300 dark:from-blue-400 dark:to-blue-600"></div>
                  )}
                  
                  <Card className="ml-0 hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm relative z-10">
                            {item.timestamp}
                          </div>
                          <div>
                            <CardTitle className="text-xl text-slate-900 dark:text-white">
                              {item.title}
                            </CardTitle>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {item.topics.map((topic) => (
                                <Badge key={topic} variant="secondary" className="text-xs">
                                  {topic}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleTimestampClick(item.timestamp, item.timeInSeconds)}
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Play className="w-4 h-4" />
                          Jump to Time
                        </Button>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Summary */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <h4 className="font-semibold text-slate-700 dark:text-slate-300">Summary</h4>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                          {item.summary}
                        </p>
                      </div>

                      <Separator />

                      {/* Key Quotes */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Quote className="w-4 h-4 text-slate-600" />
                          <h4 className="font-semibold text-slate-700 dark:text-slate-300">Key Quotes</h4>
                        </div>
                        <div className="space-y-2">
                          {item.keyQuotes.map((quote, quoteIndex) => (
                            <blockquote
                              key={quoteIndex}
                              className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-r-lg"
                            >
                              <p className="text-slate-700 dark:text-slate-300 italic">
                                "{quote}"
                              </p>
                            </blockquote>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center text-slate-600 dark:text-slate-400">
            <p>Interactive Video Analysis Timeline</p>
            <p className="text-sm mt-2">
              Click on timestamps to jump to specific parts of the video
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;